import React, { useMemo } from "react";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import { RiskLevelType } from "components/date-block/models";
import ActivityOffsetTag from "components/activity-offset-tag";
import CriticalActivityTag from "../critical-activity-tag";
import SubmittalGroupDiffV6 from "./SubmittalGroupDiffV6";
import MaterialsGroupDiffV6 from "./MaterialGroupDiffV6";
import { TSubmittalGridRow, TMaterialGridRow } from "./grid-utils";
import { TActivitiesV3, TLinkedSubmittal, TLinkedMaterial } from "./models";
import { getRiskAssessmentForEntity } from "./utils";

function computeActivityInfo(entityData: any) {
  if (!entityData) return {};

  let newActivity: any = {};
  let oldActivity: any = {};

  if (entityData?.old_governing_task) {
    const oldGoverningTasks: any[] = entityData?.old_governing_task;
    oldActivity = oldGoverningTasks?.length ? oldGoverningTasks[0] : null;
  }

  if (entityData?.new_governing_task) {
    const newGoverningTasks: any[] = entityData?.new_governing_task;
    newActivity = newGoverningTasks?.length ? newGoverningTasks[0] : null;
  }

  if (entityData?.old_governing_material?.length) {
    const oldGoverningTask: any[] =
      entityData?.old_governing_material[0].governing_task;
    const governingTask = oldGoverningTask?.length ? oldGoverningTask[0] : null;
    oldActivity = governingTask;
  }

  if (entityData?.new_governing_material?.length) {
    const newGoverningTask: any[] =
      entityData.new_governing_material[0].governing_task;
    const governingTask = newGoverningTask?.length ? newGoverningTask[0] : null;
    newActivity = governingTask;
  }
  return {
    old_critical: oldActivity?.critical,
    old_float:
      oldActivity?.total_slack != null
        ? Math.floor(oldActivity.total_slack / 8)
        : "",
    old_offset_duration: oldActivity?.offset_duration,
    old_linked_offset_date: oldActivity?.linked_offset_date,
    linked_to_end_date: oldActivity?.linked_to_end_date,
    new_critical: newActivity?.critical,
    new_float:
      newActivity?.total_slack != null
        ? Math.floor(newActivity.total_slack / 8)
        : "",
    new_offset_duration: newActivity?.offset_duration,
    new_linked_offset_date: newActivity?.linked_offset_date
  };
}

const ActivityInfo = React.memo(function ActivityInfoInner({
  activity,
  submittal_id,
  material_id
}: {
  activity: TActivitiesV3;
  submittal_id?: string;
  material_id?: string;
}) {
  const activityInfo = useMemo(() => {
    if (submittal_id && activity?.linked_submittals?.length) {
      const submittalData = activity.linked_submittals.find(
        (submittal: TLinkedSubmittal) => submittal.submittal_id === submittal_id
      );
      return computeActivityInfo(submittalData);
    }
    if (material_id && activity?.linked_materials?.length) {
      const materialData = activity.linked_materials.find(
        (material: TLinkedMaterial) => material.material_id === material_id
      );
      return computeActivityInfo(materialData);
    }
    return {} as any;
  }, [activity, submittal_id, material_id]);

  const titleDiv = (
    <span className="m-0">
      <span className="text-ciq-gray-900">Impacting Activity:</span>
      <i className="mr-2 pl-2">{activity.id}</i>
      {activity.text}
    </span>
  );

  if (activity.deleted) {
    return (
      <div className="impactingActivity">
        {titleDiv}
        <div className="text-right">
          Linked activity has been removed in the selected schedule. Please
          relink to a suitable activity, if required.
        </div>
      </div>
    );
  }

  const activityInfoDiv = (
    title1: string,
    title2: string,
    value: any,
    float: number,
    isCritical: boolean,
    isOldActivity: boolean,
    offsetDuration: any,
    linkedOffsetDate: any
  ) => {
    const parentDivClass = isOldActivity ? "bg-gray-50" : "bg-blue-50";
    const verticalLineDiv = isOldActivity ? "bg-gray-200" : "bg-blue-200";
    const floatClass = isCritical
      ? "text-black text-opacity-85 font-bold"
      : "text-black text-opacity-60 font-normal";
    return (
      <div className="flex flex-col space-y-1 w-full mt-6">
        <div className="flex justify-between items-end w-full h-6">
          <div className="text-black text-opacity-60 font-medium text-xs">
            {title1}
          </div>
          {isCritical && (
            <div>
              <CriticalActivityTag />
            </div>
          )}
        </div>
        <div
          className={`flex justify-between w-full text-sm h-[38px] ${parentDivClass}`}
        >
          <div className="flex items-center space-x-2 w-full px-2">
            <div className={`-ml-2 h-full w-1 ${verticalLineDiv}`} />
            <div className="flex truncate space-x-1">
              <span className="text-ciq-gray-950 font-medium ">{title2}:</span>{" "}
              <span className="text-black text-opacity-60 font-normal">
                {DateUtils.format(value, DATE_FORMAT_MMDDYYYY)}
              </span>
              {offsetDuration && (
                <span>
                  <ActivityOffsetTag
                    task={{
                      offset_duration: offsetDuration,
                      linked_offset_date: linkedOffsetDate
                    }}
                    isShowDate
                  />
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2 mr-2 h-full">
            <div className="text-ciq-gray-950 font-medium">Float:</div>
            <div className={floatClass}>{float}</div>
          </div>
        </div>
      </div>
    );
  };

  const beforeDate = activityInfo?.linked_to_end_date
    ? activity?.old_end_date
    : activity?.old_start;

  const afterDate = activityInfo?.linked_to_end_date
    ? activity?.new_end_date
    : activity?.new_start;

  const delta = activityInfo?.linked_to_end_date
    ? activity?.end_date_delta
    : activity?.delta;

  return (
    <div className="flex flex-col">
      {titleDiv}
      <div className="grid grid-cols-5 gap-2">
        <div className="flex col-start-2 col-end-5 space-x-6">
          {activityInfoDiv(
            "Before",
            "Old Activity Date",
            beforeDate,
            activityInfo.old_float,
            activityInfo.old_critical,
            true,
            activityInfo?.old_offset_duration,
            activityInfo?.old_linked_offset_date
          )}
          {activityInfoDiv(
            "After",
            "New Activity Date",
            afterDate,
            activityInfo.new_float,
            activityInfo.new_critical,
            false,
            activityInfo?.new_offset_duration,
            activityInfo?.new_linked_offset_date
          )}

          <div className="flex-col font-medium mt-7">
            <div className="text-xs text-black text-opacity-60 mt-1">Delta</div>
            <div className="flex space-x-1 text-sm text-black text-opacity-85 mt-3">
              <span>{delta}</span>
              <span>
                {(delta && delta > 1) || (delta && delta < -1) ? "days" : "day"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export const SubmittalImpactDetailCellRenderer = React.memo(
  function SubmittalImpactDetailCellRendererInner(params: any) {
    const data = params.data as TSubmittalGridRow;
    const { _submittal: submittal, _activity: activity } = data;
    const projectId = params.context?.projectId;
    const riskAssessment = getRiskAssessmentForEntity(submittal?.SDB);

    return (
      <div className="p-4">
        <div className="impactBoxContainerContent">
          {riskAssessment ? (
            <div>
              <span className="mr-1 text-ciq-gray-900">
                Risk assessment after Schedule Change:
              </span>
              <span
                className={
                  riskAssessment.level === RiskLevelType.High
                    ? "text-ciq-orange-700"
                    : ""
                }
              >
                {riskAssessment.text}
              </span>
            </div>
          ) : null}
          <ActivityInfo
            activity={activity}
            submittal_id={submittal.submittal_id}
          />
        </div>
        <div className="impactBoxContainerContent">
          <SubmittalGroupDiffV6
            submittal={submittal}
            activityComparison={activity}
            projectId={projectId}
          />
        </div>
      </div>
    );
  }
);

export const MaterialImpactDetailCellRenderer = React.memo(
  function MaterialImpactDetailCellRendererInner(params: any) {
    const data = params.data as TMaterialGridRow;
    const { _material: material, _activity: activity } = data;
    const projectId = params.context?.projectId;
    const riskAssessment = getRiskAssessmentForEntity(material?.MDB);

    return (
      <div className="p-4">
        <div className="impactBoxContainerContent">
          {riskAssessment ? (
            <div>
              <span className="mr-1 text-ciq-gray-900">Risk assessment:</span>
              <span
                className={
                  riskAssessment.level === RiskLevelType.High
                    ? "text-ciq-orange-700"
                    : ""
                }
              >
                {riskAssessment.text}
              </span>
            </div>
          ) : null}
          <ActivityInfo
            activity={activity}
            material_id={material.material_id}
          />
        </div>
        <div className="impactBoxContainerContent">
          <MaterialsGroupDiffV6
            material={material}
            activityComparison={activity}
            projectId={projectId}
          />
        </div>
      </div>
    );
  }
);
